import React, { useEffect, useMemo, useState } from "react";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import Nav from "../master/NavBar";
import Header from "./HostelHeader";
import {
  GetHostelList,
  HostelBedsList,
  HostelRoomList,
  HostelStudentList,
} from "../../api/HostelService";
import HostelStudentIdCardVertical from "../components/HostelStudentIdCardVertical";
import WelcomeBar from "./WelcomeBar";
import { buildPrintDocument } from "./hostelIdCardPrintStyles";
import HostelStudentIdCardModern from "../components/HostelStudentIdCardModern";
import usePrivateLink from "../config/usePrivateLink";

// Swap this single import (and the component below) to switch the card
// design used across the page and print flow. Two other designs are
// available as drop-in replacements: HostelStudentIdCard1 (horizontal, red)
// and HostelStudentIdCardModern (badge-style).
const CardComponent = HostelStudentIdCardModern;

export default function StudentIdCard() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [beds, setBeds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [data, setData] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [filters, setFilters] = useState({
    hostel_id: localStorage.getItem("hostel_id") || "",
    room_id: "",
    bed_id: "",
  });

  const getHostelList = async () => {
    setLoading(true);
    try {
      const response = await GetHostelList(`limit=all`);
      if (response.data?.status) {
        setHostels(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getHostelRoomsList = async () => {
    setLoading(true);
    let addUrl = `limit=all`;
    if (filters?.hostel_id) {
      addUrl += `&hostel_id=${filters?.hostel_id}`;
    }
    try {
      const response = await HostelRoomList(addUrl);
      if (response.data?.status) {
        setRooms(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getHostelBedsList = async () => {
    setLoading(true);
    let addUrl = `limit=all`;
    if (filters?.hostel_id) {
      addUrl += `&hostel_id=${filters?.hostel_id}`;
    }
    if (filters?.room_id) {
      addUrl += `&room_id=${filters?.room_id}`;
    }
    try {
      const response = await HostelBedsList(addUrl);
      if (response.data?.status) {
        setBeds(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getHostelStudents = async () => {
    setLoading(true);
    let addUrl = "status=1&limit=all";
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });
    try {
      const response = await HostelStudentList(addUrl);
      if (response.data?.status) {
        setData(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // Keep the hostel filter in sync with whatever another part of the app
  // (e.g. a global hostel switcher) writes to localStorage.
  useEffect(() => {
    const handleStorageChange = () => {
      const hostel_id = localStorage.getItem("hostel_id") || "";
      setFilters((prev) => ({ ...prev, hostel_id, room_id: "", bed_id: "" }));
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    getHostelList();
  }, []);

  useEffect(() => {
    getHostelRoomsList();
  }, [filters?.hostel_id]);

  useEffect(() => {
    getHostelBedsList();
  }, [filters?.hostel_id, filters?.room_id]);

  useEffect(() => {
    if (filters?.hostel_id) {
      getHostelStudents();
    } else {
      setData([]);
    }
  }, [filters]);

  // Whenever the underlying student list changes, drop any stale selection.
  useEffect(() => {
    setSelectedStudentId("");
    setStudents([]);
  }, [data]);

  const handleHostelChange = (value) => {
    setFilters({ hostel_id: value, room_id: "", bed_id: "" });
  };

  const handleRoomChange = (value) => {
    setFilters((prev) => ({ ...prev, room_id: value, bed_id: "" }));
  };

  const handleBedChange = (value) => {
    setFilters((prev) => ({ ...prev, bed_id: value }));
  };

  const handleStudentSelect = (value) => {
    setSelectedStudentId(value);
    if (value === "all") {
      setStudents(data || []);
    } else if (value) {
      setStudents((data || []).filter((s) => String(s?.id) === value));
    } else {
      setStudents([]);
    }
  };

  const handlePrint = async () => {
    const printSource = document.getElementById("PrintDiv");
    if (!students?.length || !printSource) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please select at least one student to print",
      );
      return;
    }

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please allow popups for this site to print ID cards",
      );
      return;
    }

    printWindow.document.write(buildPrintDocument(printSource.innerHTML));
    printWindow.document.close();

    // Wait for every image (logo, profile photo, signature) in the popup to
    // finish loading before invoking print, so cards never print blank.
    const waitForImages = () =>
      new Promise((resolve) => {
        const images = printWindow.document.images;
        if (!images.length) {
          resolve();
          return;
        }
        let settled = 0;
        const markSettled = () => {
          settled += 1;
          if (settled >= images.length) resolve();
        };
        Array.from(images).forEach((img) => {
          if (img.complete) {
            markSettled();
          } else {
            img.addEventListener("load", markSettled);
            img.addEventListener("error", markSettled);
          }
        });
        // Safety net in case an image never fires load/error.
        setTimeout(resolve, 3000);
      });

    printWindow.onload = async () => {
      await waitForImages();
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onafterprint = () => printWindow.close();
  };

  const hasStudentOptions = useMemo(() => (data?.length ?? 0) > 0, [data]);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <WelcomeBar />
                <div className="row">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-3 align-items-end">
                          <div className="col-12">
                            <h4 className="text-primary m-0">
                              Student ID Card
                            </h4>
                            <hr />
                          </div>

                          <div className="col-md-2 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="hostel_id"
                                id="hostel_id"
                                className="form-select"
                                value={filters?.hostel_id || ""}
                                onChange={(e) =>
                                  handleHostelChange(e.target.value)
                                }
                              >
                                <option value="">All</option>
                                {hostels?.map((hostel) => (
                                  <option key={hostel?.id} value={hostel?.id}>
                                    {hostel?.name}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="hostel_id">Select Hostel</label>
                            </div>
                          </div>

                          <div className="col-md-2 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="room_id"
                                id="room_id"
                                className="form-select"
                                value={filters?.room_id || ""}
                                onChange={(e) =>
                                  handleRoomChange(e.target.value)
                                }
                              >
                                <option value="">All</option>
                                {rooms?.map((room) => (
                                  <option key={room?.id} value={room?.id}>
                                    {room?.room_no}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="room_id">Select Room</label>
                            </div>
                          </div>

                          <div className="col-md-2 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="bed_id"
                                id="bed_id"
                                className="form-select"
                                value={filters?.bed_id || ""}
                                onChange={(e) =>
                                  handleBedChange(e.target.value)
                                }
                              >
                                <option value="">All</option>
                                {beds?.map((bed) => (
                                  <option key={bed?.id} value={bed?.id}>
                                    {bed?.bed_no}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="bed_id">Select Bed</label>
                            </div>
                          </div>

                          {hasStudentOptions && (
                            <div className="col-md-3 col-sm-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  name="student_id"
                                  id="student_id"
                                  value={selectedStudentId}
                                  onChange={(e) =>
                                    handleStudentSelect(e.target.value)
                                  }
                                >
                                  <option value="" disabled>
                                    Select Student
                                  </option>
                                  <option value="all">All</option>
                                  {data?.map((student) => (
                                    <option
                                      key={student?.id}
                                      value={student?.id}
                                    >
                                      {student?.student_name} (
                                      {student?.registration_no})
                                    </option>
                                  ))}
                                </select>
                                <label htmlFor="student_id">
                                  Select Student
                                </label>
                              </div>
                            </div>
                          )}

                          <div className="col-md-2 col-sm-6">
                            {hasAccess("/hostel/student/id/card/print") && (

                              <button
                                className="btn btn-primary w-100"
                                type="button"
                                onClick={handlePrint}
                                disabled={!students?.length}
                              >
                                Print ID Cards
                              </button>
                            )}
                          </div>

                          <div className="col-12">
                            <hr />
                          </div>

                          <div
                            className="col-12 a4-page d-flex flex-wrap justify-content-center align-content-center gap-6"
                            id="PrintDiv"
                          >
                            {students?.map((student) => (
                              <CardComponent data={student} key={student?.id} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
